import { useRef, useState } from "react";
import { useDiorama } from "../../../contexts/DioramaContext";
import CharacterPreview from "../CharacterPreview";
import SVGWrapper from "../../SVGWrapper";
import SVGColorFilter from "../../SVGColorFilter";
import ItemRenderer from "../../ItemRenderer";

// Character customization options
const skinTones = [
	{ id: "light", color: "#FFDBAC", name: "Light" },
	{ id: "medium", color: "#F1C27D", name: "Medium" },
	{ id: "tan", color: "#C68642", name: "Tan" },
	{ id: "dark", color: "#8D5524", name: "Dark" },
];

const irisColors = [
	{ id: "blue", color: "#3B82F6", name: "Blue" },
	{ id: "green", color: "#10B981", name: "Green" },
	{ id: "brown", color: "#D97706", name: "Brown" },
	{ id: "hazel", color: "#8B5CF6", name: "Hazel" },
];

const defaultOption = { id: "none", style: "", name: "None" };

// Default colors
const defaultValues = {
	skinTone: skinTones[0],
	top: { ...defaultOption, color: "#3B82F6" }, // Blue
	bottom: { ...defaultOption, color: "#374151" }, // Dark gray
	hair: { ...defaultOption, color: "#D97706" }, // Brown
	irisColor: irisColors[0], // Blue
	accessories: [{ ...defaultOption }], // None
};

const CharacterSelector: React.FC = () => {
	const { state, dispatch } = useDiorama();
	const myRef = useRef<HTMLDivElement | null>(null);

	const executeScroll = () => myRef.current?.scrollIntoView();
	const executeScrollScene = () => window.scrollTo(0, 0);

	const charOptions = state.background?.characters;

	// State for step-by-step customization
	const [customizationStep, setCustomizationStep] = useState<number>(1);

	// Character customization state
	const [characterTemplate, setCharacterTemplate] =
		useState<CharacterTemplate>();
	const [skinTone, setSkinTone] = useState(defaultValues.skinTone);
	const [irisColor, setIrisColor] = useState(defaultValues.irisColor);
	const [hair, setHair] = useState<Style>(defaultValues.hair);
	const [top, setTop] = useState<Style>(defaultValues.top);
	const [bottom, setBottom] = useState<Style>(defaultValues.bottom);
	const [accessories, setAccessories] = useState<Style[]>([]);

	// Helper functions for navigation
	const goToNextStep = () => {
		if (customizationStep < 7) setCustomizationStep(customizationStep + 1);
		executeScroll();
	};

	const goToPrevStep = () => {
		if (customizationStep > 1) setCustomizationStep(customizationStep - 1);
		executeScroll();
	};

	// Add custom character to scene
	const handleAddCustomCharacter = () => {
		// Create a unique ID for this character configuration
		const randomId = Math.random().toString(36).substring(2, 9);
		const configId = `${characterTemplate?.id}-${irisColor.id}-${skinTone.id}-${top.id}-${bottom.id}-${randomId}`;

		if (!characterTemplate) return;

		// In a real app, you would serialize the character configuration here
		// and either generate/save an image or keep the configuration to render later
		const customization = {
			template: characterTemplate,
			skinTone: skinTone,
			irisColor: irisColor,
			hair,
			top,
			bottom,
			accessories: accessories, // Use the accessories array directly
		};

		// get filled slots
		const filledSlotsIds = state.characters.map((char) => char.slot.id);

		// Find the first available slot
		const slotPos = state.background?.characters.slots.find(
			(slot) => !filledSlotsIds.includes(slot.id),
		);

		if (slotPos === undefined) {
			alert("No available slots for characters.");
			return;
		}

		const newCharacter: Character = {
			id: configId,
			image: characterTemplate.body,
			slot: slotPos,
			customization: customization, // Store the config for later rendering
		};

		dispatch({ type: "ADD_CHARACTER", character: newCharacter });

		// Reset customization state
		setCharacterTemplate(undefined);
		setSkinTone(defaultValues.skinTone);
		setIrisColor(defaultValues.irisColor);
		setHair(defaultValues.hair);
		setTop(defaultValues.top);
		setBottom(defaultValues.bottom);
		setAccessories([]);

		// Reset to first step after adding
		setCustomizationStep(1);
		executeScrollScene();
	};

	// Remove character from scene
	const handleRemoveCharacter = (id: string) => {
		setAccessories([]);
		setCharacterTemplate(undefined);
		setSkinTone(defaultValues.skinTone);
		setIrisColor(defaultValues.irisColor);
		setHair(defaultValues.hair);
		setTop(defaultValues.top);
		setBottom(defaultValues.bottom);
		dispatch({ type: "REMOVE_CHARACTER", id });
		executeScroll();
	};

	// Add or toggle an accessory
	const handleAccessoryToggle = (acc: Style) => {
		if (acc.id === "none") {
			// "None" option clears all accessories
			setAccessories([]);
			return;
		}

		// Check if this accessory is already selected
		const existingIndex = accessories.findIndex((item) => item.id === acc.id);

		if (existingIndex >= 0) {
			// If already selected, update its properties (like color)
			const updatedAccessories = [...accessories];
			updatedAccessories[existingIndex] = acc;
			setAccessories(updatedAccessories);
		} else {
			// If not selected, add it to the array
			setAccessories([...accessories, acc]);
		}
	};

	// Remove a specific accessory
	const handleRemoveAccessory = (accId: string) => {
		setAccessories(accessories.filter((acc) => acc.id !== accId));
	};

	// Render current customization step
	const renderCustomizationStep = () => {
		// const viewBox = `0 0 ${characterTemplate?.width} ${characterTemplate?.height}`;
		switch (customizationStep) {
			case 1: // Template selection
				return (
					<div className="space-y-4">
						<h3 className="font-medium">Select Template</h3>
						<div className="flex border rounded border-gray-400 overflow-x-scroll p-1">
							{charOptions?.templates.map((template) => (
								<div
									key={template.body}
									className={`py-3 px-4 m-1 rounded items-center border-2 hover:border-blue-400 ${
										characterTemplate?.body === template.body
											? "border-amber-600 bg-blue-100"
											: "border-gray-400"
									}`}
									onClick={() => setCharacterTemplate(template)}
								>
									<CharacterPreview
										template={template}
										iris={irisColor}
										skinTone={skinTone}
									/>
								</div>
							))}
						</div>
					</div>
				);

			case 2: // Skin tone selection
				return (
					<div className="space-y-4">
						<h3 className="font-medium">Select Skin Tone</h3>
						<div className="grid grid-cols-2 gap-3">
							{skinTones.map((skin) => (
								<div
									key={skin.id}
									className={`p-2 border-2 rounded cursor-pointer flex items-center ${
										skinTone.id === skin.id
											? "border-amber-600 bg-blue-100"
											: "border-gray-400"
									}`}
									onClick={() => setSkinTone(skin)}
								>
									<div
										className="w-8 h-8 rounded-full mr-2"
										style={{ backgroundColor: skin.color }}
									/>
									<span>{skin.name}</span>
								</div>
							))}
						</div>
					</div>
				);

			case 3: // Eye selection
				return (
					<div className="space-y-4">
						<h3 className="font-medium">Select Eye Color</h3>

						<div>
							<h4 className="text-sm font-medium mb-2">Eye Color</h4>
							<div className="flex">
								<input
									type="color"
									value={irisColor.color}
									onChange={(e) =>
										setIrisColor({ ...irisColor, color: e.target.value })
									}
									placeholder="Select Eye Color"
									className="flex-1 h-10 rounded-l cursor-pointer"
								/>
								<div className="colorpicker px-5 border rounded-r mt-1 h-8 border-gray-400 border-l-0" />
							</div>
						</div>
					</div>
				);

			case 4: // Hair selection
				const hairOptions = [
					defaultOption,
					...(characterTemplate?.styles.hair ?? []),
				];
				return (
					<div className="space-y-4">
						<h3 className="font-medium">Select Hair</h3>
						<div className="grid grid-cols-2 gap-3 mb-4">
							{hairOptions.map((style) => (
								<div
									key={style.id}
									className={`p-2 border-2 rounded cursor-pointer ${
										hair.id === style.id
											? "border-amber-600 bg-blue-100"
											: "border-gray-400"
									}`}
									onClick={() =>
										style.id === "none"
											? setHair(defaultValues.hair) // Reset to default "None" state
											: setHair({
													...hair,
													id: style.id,
													style: style.style,
												})
									}
								>
									<div className="flex justify-center items-center mb-1">
										<SVGWrapper viewBox="0 0 32 32" width="64" height="64">
											<defs>
												<SVGColorFilter id="hair" color={hair.color!} />
											</defs>
											{style.id !== "none" && (
												<ItemRenderer
													item={{
														...hair,
														id: style.id,
														style: style.style,
													}}
													filterId="hair"
												/>
											)}
										</SVGWrapper>
									</div>
									<p className="text-center">{style.name}</p>
								</div>
							))}
						</div>

						<div>
							<h4 className="text-sm font-medium mb-2">Hair Color</h4>
							<div className="flex">
								<input
									type="color"
									value={hair.color}
									onChange={(e) => setHair({ ...hair, color: e.target.value })}
									className="flex-1 h-10 rounded-l cursor-pointer"
								/>
								<div className="colorpicker px-5 border rounded-r mt-1 h-8 border-gray-400 border-l-0" />
							</div>
						</div>
					</div>
				);

			case 5: // Top clothing selection
				const topOptions = [
					defaultOption,
					...(characterTemplate?.styles.top ?? []),
				];
				return (
					<div className="space-y-4">
						<h3 className="font-medium">Select Top</h3>
						<div className="grid grid-cols-2 gap-3 mb-4">
							{topOptions.map((style) => (
								<div
									key={style.id}
									className={`p-2 border-2 rounded cursor-pointer ${
										top.id === style.id
											? "border-amber-600 bg-blue-100"
											: "border-gray-400"
									}`}
									onClick={() =>
										style.id === "none"
											? setTop(defaultValues.top) // Reset to default "None" state
											: setTop({
													...top,
													id: style.id,
													style: style.style,
												})
									}
								>
									<div className="flex justify-center items-center mb-1">
										<SVGWrapper viewBox="0 0 32 64" width="64" height="128">
											<defs>
												<SVGColorFilter id="top" color={top.color!} />
											</defs>
											{style.id !== "none" && (
												<ItemRenderer
													item={{
														...top,
														id: style.id,
														style: style.style,
													}}
													filterId="top"
												/>
											)}
										</SVGWrapper>
									</div>
									<p className="text-center">{style.name}</p>
								</div>
							))}
						</div>

						<div>
							<h4 className="text-sm font-medium mb-2">Top Color</h4>
							<div className="flex">
								<input
									type="color"
									value={top.color}
									onChange={(e) => setTop({ ...top, color: e.target.value })}
									className="flex-1 h-10 rounded-l cursor-pointer"
								/>
								<div className="colorpicker px-5 border rounded-r mt-1 h-8 border-gray-400 border-l-0" />
							</div>
						</div>
					</div>
				);

			case 6: // Bottom clothing selection
				const bottomOptions = [
					defaultOption,
					...(characterTemplate?.styles.bottom ?? []),
				];
				return (
					<div className="space-y-4">
						<h3 className="font-medium">Select Bottom</h3>
						<div className="grid grid-cols-2 gap-3 mb-4">
							{bottomOptions.map((style) => (
								<div
									key={style.id}
									className={`p-2 border-2 rounded cursor-pointer ${
										bottom.id === style.id
											? "border-amber-600 bg-blue-100"
											: "border-gray-400"
									}`}
									onClick={() =>
										style.id === "none"
											? setBottom(defaultValues.bottom) // Reset to default "None" state
											: setBottom({
													...bottom,
													id: style.id,
													style: style.style,
												})
									}
								>
									<div className="flex justify-center items-center mb-1">
										<SVGWrapper viewBox="0 0 32 96" width="64" height="192">
											<defs>
												<SVGColorFilter id="bottom" color={bottom.color!} />
											</defs>
											{style.id !== "none" && (
												<ItemRenderer
													item={{
														...bottom,
														id: style.id,
														style: style.style,
													}}
													filterId="bottom"
												/>
											)}
										</SVGWrapper>
									</div>
									<p className="text-center">{style.name}</p>
								</div>
							))}
						</div>

						<div>
							<h4 className="text-sm font-medium mb-2">Bottom Color</h4>
							<div className="flex">
								<input
									type="color"
									value={bottom.color}
									onChange={(e) =>
										setBottom({ ...bottom, color: e.target.value })
									}
									className="flex-1 h-10 rounded-l cursor-pointer"
								/>

								<div className="colorpicker px-5 border rounded-r mt-1 h-8 border-gray-400 border-l-0" />
							</div>
						</div>
					</div>
				);

			case 7: // Accessories selection
				const accessoryOptions = [
					defaultOption,
					...(characterTemplate?.styles.accessories ?? []),
				];
				return (
					<div className="space-y-4">
						<h3 className="font-medium">Select Accessories</h3>

						<div className="grid grid-cols-2 gap-3 mb-4">
							{accessoryOptions.map((acc) => {
								// Check if this accessory is selected
								const isSelected = accessories.some((a) => a.id === acc.id);

								return (
									<div
										key={acc.id}
										className={`p-2 border-2 rounded cursor-pointer ${
											isSelected
												? "border-amber-600 bg-blue-100"
												: "border-gray-400"
										}`}
										onClick={() =>
											handleAccessoryToggle({
												...acc,
												color: "#fd5c63",
											})
										}
									>
										<div className="flex justify-center items-center mb-1">
											{acc.id !== "none" && (
												<CharacterPreview
													template={characterTemplate!}
													skinTone={skinTone}
													iris={irisColor}
													hair={hair}
													top={top}
													bottom={bottom}
													accessories={
														acc.id !== "none"
															? [{ ...acc, color: "#fd5c63" }]
															: []
													}
													size="md"
												/>
											)}
										</div>
										<p className="text-center">{acc.name}</p>
									</div>
								);
							})}
						</div>
					</div>
				);

			default:
				return null;
		}
	};

	// Character preview display
	const renderCharacterPreview = () => (
		<div className="bg-gray-200 border-2 border-blue-400 rounded-lg p-4 flex items-center justify-center shadow-sm">
			{characterTemplate && (
				<CharacterPreview
					template={characterTemplate}
					skinTone={skinTone}
					iris={irisColor}
					hair={hair}
					top={top}
					bottom={bottom}
					accessories={accessories} // Pass the full accessories array
					size="lg"
				/>
			)}
		</div>
	);

	// Render characters in the scene
	const renderCharactersInScene = () => {
		if (!state.characters.length) return null;

		return (
			<div>
				<h3 className="text-lg font-semibold mb-4">
					Characters in scene: {state.characters.length}/
					{state.background?.characters.max}
				</h3>
				<div className="flex gap-2 flex-wrap">
					{state.characters.map((char) => (
						<div
							key={char.id}
							className="relative w-32 h-32 p-2 items-center border-2 border-gray-400 rounded bg-gray-100"
						>
							{/* Check if this is a custom character with config */}
							{char.customization ? (
								<CharacterPreview
									template={char.customization.template}
									skinTone={char.customization.skinTone}
									hair={char.customization.hair}
									top={char.customization.top}
									bottom={char.customization.bottom}
									accessories={char.customization.accessories}
									iris={char.customization.irisColor}
									size="sm"
								/>
							) : (
								<img
									src={char.image}
									alt="Character"
									className="w-full h-full object-contain"
								/>
							)}
							<button
								type="button"
								className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs cursor-pointer"
								onClick={() => handleRemoveCharacter(char.id)}
							>
								<svg
									xmlns="http://www.w3.org/2000/svg"
									fill="none"
									viewBox="0 0 24 24"
									strokeWidth="1.5"
									stroke="currentColor"
									className="size-5"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										d="M6 18L18 6M6 6l12 12"
									/>
								</svg>
							</button>
						</div>
					))}
				</div>
			</div>
		);
	};

	return (
		<div>
			<h2 className="text-xl font-semibold mb-4">Step 2: Add Characters</h2>
			<p className="mb-4" ref={myRef}>
				Select characters and customize their appearance for your diorama
			</p>

			{state.background?.characters.max !== state.characters.length && (
				<>
					<p className="mb-4">Customize your character</p>
					<div className="flex flex-col lg:flex-row gap-4 mb-6">
						{/* Left side: Customization options */}
						<div className="lg:w-2/3">
							{renderCustomizationStep()}

							{/* Navigation buttons */}
							<div className="flex justify-between mt-6">
								<button
									type="button"
									onClick={goToPrevStep}
									disabled={customizationStep === 1}
									className={`px-4 py-2 rounded ${
										customizationStep === 1
											? "bg-gray-300 cursor-not-allowed"
											: "bg-gray-500 text-white hover:bg-gray-600"
									}`}
								>
									Back
								</button>

								{customizationStep < 7 ? (
									<button
										type="button"
										onClick={goToNextStep}
										disabled={!characterTemplate}
										className={`px-4 py-2 rounded ${
											!characterTemplate
												? "bg-gray-300 cursor-not-allowed"
												: "bg-blue-500 text-white hover:bg-blue-600"
										}`}
									>
										Next
									</button>
								) : (
									<button
										type="button"
										onClick={handleAddCustomCharacter}
										disabled={!characterTemplate}
										className={`px-4 py-2 rounded ${
											!characterTemplate
												? "bg-gray-300 cursor-not-allowed"
												: "bg-green-500 text-white hover:bg-green-600"
										}`}
									>
										Add to Scene
									</button>
								)}
							</div>
						</div>

						{/* Right side: Character preview */}
						<div className="lg:w-1/3">
							<h3 className="font-medium mb-3 text-center">Preview</h3>
							{characterTemplate ? (
								renderCharacterPreview()
							) : (
								<div className="bg-gray-200 border-2 border-blue-400 rounded-lg p-4 flex items-center justify-center shadow-sm text-gray-600">
									Select a character template to begin
								</div>
							)}

							<div>
								{accessories.length > 0 && (
									<div className="mb-4">
										<h4 className="text-sm font-medium my-2">
											Selected Accessories:
										</h4>
										<div className="flex flex-wrap gap-2">
											{accessories.map((acc) => (
												<div
													key={acc.id}
													className="bg-blue-50 px-2 py-1 rounded border border-blue-200 flex items-center"
												>
													<span className="mr-1">
														{
															characterTemplate?.styles.accessories.find(
																(a) => a.id === acc.id,
															)?.name
														}
													</span>
													<div className="flex">
														<input
															type="color"
															value={acc.color}
															onChange={(e) => {
																const updatedAccessories = accessories.map(
																	(item) =>
																		item.id === acc.id
																			? { ...item, color: e.target.value }
																			: item,
																);
																setAccessories(updatedAccessories);
															}}
															className="h-8 flex-1 cursor-pointer rounded-l border-gray-400"
														/>
														<div className="colorpicker px-3 border rounded-r h-8 border-gray-400 border-l-0" />
													</div>
													<button
														onClick={() => handleRemoveAccessory(acc.id)}
														className="bg-red-500 font-bold rounded-full w-5 h-5 ml-3 text-white cursor-pointer"
													>
														<svg
															xmlns="http://www.w3.org/2000/svg"
															fill="none"
															viewBox="0 0 24 24"
															strokeWidth="1.5"
															stroke="currentColor"
															className="size-5"
														>
															<path
																strokeLinecap="round"
																strokeLinejoin="round"
																d="M6 18L18 6M6 6l12 12"
															/>
														</svg>
													</button>
												</div>
											))}
										</div>
									</div>
								)}
							</div>
						</div>
					</div>
				</>
			)}

			{/* Selected Characters */}
			{renderCharactersInScene()}
		</div>
	);
};

export default CharacterSelector;
